/**
 * Una condición del filtro de un público (SPEC-BD §6.4). Cada tipo tiene su forma: etiquetas, campo
 * personalizado, carga de origen, sucursal, fecha de alta, email y consentimiento.
 *
 * Las opciones salen de `GET /base/opciones`: se elige de lo que existe en la base (claves de etiqueta,
 * ids de carga, sucursales normalizadas), nunca un texto libre que el backend no sabría resolver. Los
 * operadores y sus textos son los de `logic/base.ts`.
 */

import { useId } from "react";
import { X } from "lucide-react";
import type { CondicionPublico, ConsentimientoContacto, OpCampo, OpcionesBase } from "../../types.base";
import { TIPOS_CONDICION, opsDeCampo, opsDeLista } from "../../logic/base";

interface Props {
  condicion: CondicionPublico;
  opciones: OpcionesBase;
  onChange: (c: CondicionPublico) => void;
  onQuitar: () => void;
  disabled?: boolean;
}

const selectCls = "h-9 rounded-xl border border-slate-200 px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50";
const inputCls = "h-9 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50";

const CONSENTIMIENTOS: { valor: ConsentimientoContacto; label: string }[] = [
  { valor: "si", label: "Con consentimiento" }, { valor: "sin_dato", label: "Sin dato" }, { valor: "no", label: "Dijo que no" },
];

export function CondicionFila({ condicion: c, opciones, onChange, onQuitar, disabled = false }: Props) {
  const titulo = TIPOS_CONDICION.find((t) => t.tipo === c.tipo)?.label ?? c.tipo;
  const idOp = useId();

  return (
    <li className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-xs font-semibold text-slate-600">{titulo}</p>
          {(c.tipo === "etiquetas" || c.tipo === "carga" || c.tipo === "sucursal") && (
            <div className="flex flex-wrap items-start gap-2">
              <label htmlFor={idOp} className="sr-only">Operador de {titulo}</label>
              <select id={idOp} value={c.op} disabled={disabled} className={selectCls}
                onChange={(e) => onChange({ ...c, op: e.target.value } as CondicionPublico)}>
                {opsDeLista(c.tipo).map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
              </select>
              <MultiSelect
                valores={c.valores}
                opciones={opcionesDeLista(c.tipo, opciones)}
                disabled={disabled}
                etiqueta={titulo}
                onChange={(valores) => onChange({ ...c, valores } as CondicionPublico)}
              />
            </div>
          )}
          {c.tipo === "campo" && <CondicionCampo c={c} opciones={opciones} onChange={onChange} disabled={disabled} />}
          {c.tipo === "alta" && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <span>entre</span>
              <input type="date" aria-label="Fecha de alta desde" value={c.desde ?? ""} disabled={disabled} className={inputCls}
                onChange={(e) => onChange({ tipo: "alta", ...(e.target.value ? { desde: e.target.value } : {}), ...(c.hasta ? { hasta: c.hasta } : {}) })} />
              <span>y</span>
              <input type="date" aria-label="Fecha de alta hasta" value={c.hasta ?? ""} disabled={disabled} className={inputCls}
                onChange={(e) => onChange({ tipo: "alta", ...(c.desde ? { desde: c.desde } : {}), ...(e.target.value ? { hasta: e.target.value } : {}) })} />
              <span className="text-xs text-slate-400">(una puede quedar vacía)</span>
            </div>
          )}
          {c.tipo === "email" && (
            <>
              <label htmlFor={idOp} className="sr-only">Email</label>
              <select id={idOp} value={c.tiene ? "si" : "no"} disabled={disabled} className={selectCls}
                onChange={(e) => onChange({ tipo: "email", tiene: e.target.value === "si" })}>
                <option value="si">tiene email</option>
                <option value="no">no tiene email</option>
              </select>
            </>
          )}
          {c.tipo === "consentimiento" && (
            <div className="flex flex-wrap gap-3" role="group" aria-label="Consentimiento">
              {CONSENTIMIENTOS.map((o) => (
                <label key={o.valor} className="inline-flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" disabled={disabled} checked={c.valores.includes(o.valor)}
                    onChange={(e) => onChange({ tipo: "consentimiento", valores: e.target.checked ? [...c.valores, o.valor] : c.valores.filter((v) => v !== o.valor) })}
                    className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                  {o.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <button type="button" onClick={onQuitar} disabled={disabled} aria-label={`Sacar la condición ${titulo}`}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex-shrink-0 disabled:opacity-50">
          <X size={16} />
        </button>
      </div>
    </li>
  );
}

function opcionesDeLista(tipo: "etiquetas" | "carga" | "sucursal", o: OpcionesBase): { valor: string; texto: string }[] {
  if (tipo === "etiquetas") return (o.etiquetas || []).map((e) => ({ valor: e.clave, texto: e.texto }));
  if (tipo === "sucursal") return (o.sucursales || []).map((s) => ({ valor: s.clave, texto: s.texto }));
  // Sólo las cargas importadas: una sin importar no tiene contactos.
  return (o.cargas || []).filter((c) => !!c.importadaAt).map((c) => ({ valor: c.id, texto: c.nombre }));
}

/** Chips de lo elegido y un `select` para sumar. Un valor que ya no está en las opciones se muestra con su id (para poder sacarlo). */
function MultiSelect({ valores, opciones, onChange, disabled, etiqueta }: {
  valores: string[]; opciones: { valor: string; texto: string }[]; onChange: (v: string[]) => void; disabled: boolean; etiqueta: string;
}) {
  const idSel = useId();
  const texto = (v: string) => opciones.find((o) => o.valor === v)?.texto ?? v;
  const libres = opciones.filter((o) => !valores.includes(o.valor));
  return (
    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
      {valores.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold pl-2.5 pr-1 py-0.5">
          {texto(v)}
          <button type="button" disabled={disabled} onClick={() => onChange(valores.filter((x) => x !== v))} aria-label={`Sacar ${texto(v)}`}
            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-violet-100 disabled:opacity-50">
            <X size={11} />
          </button>
        </span>
      ))}
      <label htmlFor={idSel} className="sr-only">Agregar a {etiqueta}</label>
      <select id={idSel} value="" disabled={disabled || !libres.length} className={selectCls}
        onChange={(e) => { if (e.target.value) onChange([...valores, e.target.value]); }}>
        <option value="">{libres.length ? "Agregar…" : opciones.length ? "No hay más" : "No hay opciones"}</option>
        {libres.map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
      </select>
    </div>
  );
}

function CondicionCampo({ c, opciones, onChange, disabled }: {
  c: Extract<CondicionPublico, { tipo: "campo" }>; opciones: OpcionesBase; onChange: (c: CondicionPublico) => void; disabled: boolean;
}) {
  const idCampo = useId();
  const idOp = useId();
  const idValor = useId();
  const campo = (opciones.campos || []).find((k) => k.clave === c.clave);
  const ops = campo ? opsDeCampo(campo.tipo) : [];
  const op = ops.find((o) => o.op === c.op);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor={idCampo} className="sr-only">Campo</label>
      <select id={idCampo} value={c.clave} disabled={disabled} className={selectCls}
        onChange={(e) => onChange({ tipo: "campo", clave: e.target.value, op: "igual" })}>
        <option value="">Elegí un campo</option>
        {(opciones.campos || []).map((k) => <option key={k.clave} value={k.clave}>{k.etiqueta}</option>)}
      </select>
      {campo && (
        <>
          <label htmlFor={idOp} className="sr-only">Operador</label>
          <select id={idOp} value={c.op} disabled={disabled} className={selectCls}
            onChange={(e) => {
              const nuevo = e.target.value as OpCampo;
              const necesita = ops.find((o) => o.op === nuevo)?.necesitaValor;
              onChange({ tipo: "campo", clave: c.clave, op: nuevo, ...(necesita && c.valor !== undefined ? { valor: c.valor } : {}) });
            }}>
            {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
          </select>
          {op?.necesitaValor && (
            <>
              <label htmlFor={idValor} className="sr-only">Valor</label>
              {campo.tipo === "numero" ? (
                <input id={idValor} type="number" inputMode="decimal" value={typeof c.valor === "number" ? String(c.valor) : ""} disabled={disabled}
                  className={`${inputCls} w-32`}
                  onChange={(e) => {
                    const n = e.target.value === "" ? NaN : Number(e.target.value);
                    onChange({ tipo: "campo", clave: c.clave, op: c.op, ...(Number.isFinite(n) ? { valor: n } : {}) });
                  }} />
              ) : campo.tipo === "fecha" ? (
                <input id={idValor} type="date" value={typeof c.valor === "string" ? c.valor : ""} disabled={disabled} className={inputCls}
                  onChange={(e) => onChange({ tipo: "campo", clave: c.clave, op: c.op, ...(e.target.value ? { valor: e.target.value } : {}) })} />
              ) : (
                <input id={idValor} value={typeof c.valor === "string" ? c.valor : ""} maxLength={200} disabled={disabled} className={`${inputCls} w-48`}
                  onChange={(e) => onChange({ tipo: "campo", clave: c.clave, op: c.op, ...(e.target.value ? { valor: e.target.value } : {}) })} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default CondicionFila;
